/**
 * 帳款明細表(`ar-detail`,未勾「依客戶分別列印」)的**列印版面**(REQ-20260901002)。
 *
 * 使用者提三件事:合計只印在最後一頁、頁數要有分母、每一頁最上面都要有抬頭。
 * 三件事其實是同一件 —— 交給瀏覽器自動分頁就三件都做不到:
 * 抬頭是普通 `<div>`(不會跨頁重複)、`<tfoot>` 每一頁都重印、總頁數 JS 讀不到。
 *
 * 所以這裡**不預測瀏覽器會在哪裡分頁,而是自己指定**:量好每一列的實際高度,
 * 裝箱成固定張數的 A4 區塊,每塊自己帶一份抬頭與表頭列,塊與塊之間掛
 * `break-after: page`。總頁數是我們決定的,不是猜的 —— 分母因此才敢印。
 *
 * REQ-20260831008 §七 已經寫明這條路的兩個前提,兩個都在本次一併滿足:
 *  (a) **表格要窄到不需要 Chrome 自動縮放** —— 儲存格水平 padding 由 `px-4` 收成
 *      `px-2`(只有這份列印版面收,螢幕那份一個像素都沒動),量到十欄的最小寬度
 *      落在可印寬之內。縮放比例 JS 讀不到,一旦被縮放,分母就會錯。
 *  (b) **接受紙張邊界固定** —— 由本檔掛的 `@page { margin: 10mm }` 指定,
 *      不支援使用者在列印對話框自訂邊界。同一份版面在 0.4in / 0.5in 下張數不同
 *      (REQ-20260831008 §三),邊界不固定就沒有可信的分母。
 *
 * **沒有引進 Paged.js 或任何 PDF 套件**(REQ-20260806002 §3.4 的既有決策)——
 * 全部是既有的 React + CSS `break-after`。
 *
 * REQ-20260901012 多了一個選填的 `summary`:表格之後、只印最後一頁的一塊。
 * 它的高度一併算進裝箱(見量測的 `footerHeight`),`paginateRows` 沒有改 ——
 * 「最後一頁塞不下就自己成為新的一頁」對它自動成立。
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ArDetailPrintHeader, type ArDetailHeaderParty } from './ArDetailPrintHeader';
import { alignClass, cellClass, type ReportColumn } from './ReportTable';

/**
 * A4 直向、`@page { margin: 10mm }` 之後剩下的內容區。
 * 列印時 CSS px 固定 1in = 96px,所以毫米換得出來,不需要在瀏覽器裡探測。
 */
const PX_PER_MM = 96 / 25.4;
/** 210mm − 左右各 10mm */
export const PAGE_CONTENT_WIDTH_MM = 190;
/** 297mm − 上下各 10mm ≈ 1046.9px */
export const PAGE_CONTENT_HEIGHT_PX = (297 - 20) * PX_PER_MM;

/**
 * 裝箱的填充比例 —— **寧可少放一列,也不要溢出到下一張紙**。
 * 溢出一列就多一張紙,我們印的分母立刻與實際張數對不上,而那正是頁碼要防的傷害
 * (REQ-20260831004 §四.1)。餘裕吸收的是量測與列印之間的零頭:
 * 頁面外層 `main` 的 padding、字型 hinting 造成的次像素差、最後一列的邊框。
 * 實測數據與這個值怎麼定的寫在 docs/REQ-20260901002 §四。
 */
export const PAGE_FILL = 0.96;

export interface PaginateInput {
  /** 每一個 `<tr>` 的實際高度(`getBoundingClientRect().height`) */
  rowHeights: number[];
  /** 抬頭區的高度 —— 每一頁都要扣 */
  headerHeight: number;
  /** 表頭列(`<thead>`)的高度 —— 每一頁都要扣 */
  theadHeight: number;
  /**
   * 最後一頁才有的東西的高度總和:合計列 + `summary` 那一塊(REQ-20260901012)。
   * 只有最後一頁要扣 —— 兩者都只掛在最後一頁,所以合成一個數字傳進來,
   * 這支純函式不需要知道最後一頁上到底放了幾種東西。
   */
  footerHeight: number;
  pageHeight?: number;
  fill?: number;
}

/**
 * 把列裝箱成頁。回傳「每一頁有哪幾個列索引」,`[]` 代表那一頁只有合計列。
 *
 * 規則:
 *  - 每一頁固定扣掉抬頭 + 表頭列的高度(兩者每頁都重印)。
 *  - 合計列只掛在**最後一頁**;最後一頁塞不下它就讓它自己成為新的一頁。
 *  - 一列再高也不會被切開 —— 塞不下就整列推到下一頁(與 `globals.css` 的
 *    `tr { break-inside: avoid }` 同一個意思,只是這裡由我們決定)。
 */
export function paginateRows({
  rowHeights,
  headerHeight,
  theadHeight,
  footerHeight,
  pageHeight = PAGE_CONTENT_HEIGHT_PX,
  fill = PAGE_FILL,
}: PaginateInput): number[][] {
  const usable = pageHeight * fill - headerHeight - theadHeight;
  const pages: number[][] = [];
  let current: number[] = [];
  let used = 0;

  for (let i = 0; i < rowHeights.length; i += 1) {
    const h = rowHeights[i] ?? 0;
    // `current.length > 0`:一列比整頁還高時也不能無限換頁,讓它自己佔一頁
    if (current.length > 0 && used + h > usable) {
      pages.push(current);
      current = [];
      used = 0;
    }
    current.push(i);
    used += h;
  }
  pages.push(current);

  // `footerHeight > 0`:沒有合計列時不該憑空多開一頁
  if (footerHeight > 0 && current.length > 0 && used + footerHeight > usable) pages.push([]);
  return pages;
}

/**
 * 只在本元件掛載期間存在的列印樣式 —— 照 `OrderPrintPage.tsx` 的既有手法
 * (REQ-20260806004):`@page` 是全域規則,寫進 `globals.css` 會連月結對帳單與
 * 另外八張報表一起改。
 *
 * 三條規則各自對應一個必要條件:
 *  - `@page`:邊界固定成 10mm,可印寬因此是確定的 190mm(前提 b)。
 *  - `main` / `header`:App shell 的 `py-6` 與 `border-b` 會把第一頁的內容往下推
 *    25px,第一頁就比其他頁少裝東西 —— 分母會錯。歸零之後每一頁的起點一致。
 *  - `[data-ar-detail-measure]`:抬頭本身是 `hidden print:block`,離屏量測是在
 *    **螢幕**上做的,不強制顯示會量到 0。只作用在離屏容器裡,螢幕外觀不受影響。
 */
const PRINT_STYLE = `
@page { size: A4; margin: 10mm; }
@media print {
  main { padding: 0 !important; }
  header { display: none !important; }
}
[data-ar-detail-measure] [data-testid='ar-detail-print-header'] { display: block !important; }
`;

/** 列印版面的儲存格水平 padding:`px-2` 是前提 (a),見檔頭 */
const CELL_PAD = 'px-2 py-2';

export interface ArDetailPrintPagesProps<T> {
  /** 沿用 `ArDetailItemTable` 的那一份,不在這裡複製第二份欄位定義 */
  columns: Array<ReportColumn<T>>;
  /** 攤平後的品項列 */
  rows: T[];
  rowKey: (row: T) => string | number;
  /** 合計列的內容,與 `columns` 等長 */
  footer: Array<ReactNode | null>;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  emptyHint: string;
  /**
   * 這一欄吃掉表格的剩餘寬度(REQ-20260901003 第 5 點)。
   * auto 版面預設把多出來的空間**按比例分給所有欄位**,日期收窄省下的位置
   * 會散給十欄;指定之後其餘欄位收斂到 max-content,剩下的全歸這一欄。
   * 量測那一份與頁面那一份是同一個 `PageTable`,所以量到的 `colWidths` 已經反映它。
   */
  growColumnKey?: string | undefined;
  /**
   * 表格之後、**只印在最後一頁**的一塊(REQ-20260901012:有標籤的合計說明區)。
   *
   * 刻意收成一個 `ReactNode`,不把 ar-detail 的欄位語意寫進這支 —— 這支是泛型的
   * (吃 `ReportColumn<T>`),REQ-20260901002 §七 預告過它可能搬去月結對帳單,
   * 而那邊的合計項目(前期累計未收帳款…)與這張表不一樣。
   *
   * 高度會被算進裝箱(見量測的 `footerHeight`),所以「最後一頁塞不下就自己成為
   * 新的一頁」對它一樣成立,`paginateRows` 一行都不用改。
   */
  summary?: ReactNode;
  /** 抬頭要的四項 —— 與 `ArDetailPrintHeader` 同一組,頁數由本元件自己算 */
  reportLabel: string;
  /**
   * 抬頭真的用得到的那六格(REQ-20260901013)。單一客戶那條路徑傳的仍是完整的
   * `PartyInfo`(結構相容);勾分組列印時每位客戶的資料從明細列上取,沒有 `id` /
   * `phones` 那些欄位,所以型別收窄到用得到的部分而不是去捏假值。
   */
  customer: ArDetailHeaderParty | null;
  from: string;
  to: string;
}

export function ArDetailPrintPages<T>({
  columns,
  rows,
  rowKey,
  footer,
  isLoading,
  isError,
  error,
  emptyHint,
  growColumnKey,
  summary,
  reportLabel,
  customer,
  from,
  to,
}: ArDetailPrintPagesProps<T>): JSX.Element {
  const measureRef = useRef<HTMLDivElement>(null);
  /**
   * 量測結果。**`signature` 跟著一起存** —— 資料換了但還沒重量到時,舊的頁面索引
   * 會指到不存在的列(`rows[i]` 是 undefined),下面只在 signature 對得上時才採用。
   *
   * `colWidths` 是表頭量到的欄寬,頁面表格改吃 `table-layout: fixed` + 這組寬度:
   * 每頁只有部分列,auto 版面會讓各頁的欄寬不一樣,商品名稱的折行位置跟著變,
   * 量到的高度就對不上實際印出來的高度。固定欄寬之後每一頁的列高與量測完全一致。
   */
  const [measured, setMeasured] = useState<{
    signature: string;
    pages: number[][];
    colWidths: number[];
  }>({ signature: '', pages: [], colWidths: [] });

  /** 載入中 / 失敗 / 沒資料:只印一頁抬頭 + 既有的三態訊息,不要印出空白頁 */
  const blank = isLoading || isError || rows.length === 0;

  /**
   * 量測的觸發條件用**字串**,不用 `rows` / `columns` 本身 —— 兩者都是呼叫端
   * 每次 render 重建的陣列,放進相依清單會每 render 重量一次、`setPages` 再觸發
   * 下一輪,直接轉成無限迴圈。
   */
  const signature = blank
    ? 'blank'
    : `${rows.map((r) => rowKey(r)).join('|')}#${columns.map((c) => c.key).join(',')}`;

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const height = (node: Element | null | undefined): number =>
      node ? node.getBoundingClientRect().height : 0;

    setMeasured({
      signature,
      pages: paginateRows({
        rowHeights: [...el.querySelectorAll('tbody tr[data-print-row]')].map((tr) => height(tr)),
        headerHeight: height(el.querySelector('[data-measure="header"]')),
        theadHeight: height(el.querySelector('thead')),
        // 只掛在最後一頁的兩塊:合計列(不在 `<tfoot>` 裡,見 `PageTable`,所以按 testid 找)
        // 加上 `summary` 那一塊 —— 少算 summary 的話最後一頁會溢出成第二張紙,分母就說謊了
        footerHeight:
          height(el.querySelector('[data-testid="ar-detail-print-total"]')) +
          height(el.querySelector('[data-measure="summary"]')),
      }),
      colWidths: [...el.querySelectorAll('thead th')].map((th) => th.getBoundingClientRect().width),
    });
  }, [signature]);

  const fresh = measured.signature === signature ? measured : null;
  // 還沒量到(第一次 render)就先全部擺一頁 —— `useLayoutEffect` 會在同一次繪製前修正
  const pageList = blank ? [[]] : (fresh?.pages ?? [[...rows.keys()]]);

  return (
    <>
      <style>{PRINT_STYLE}</style>

      {/*
        離屏量測用的那一份。放在 `hidden print:block` 的外面 —— 塞在裡面的話
        螢幕上整塊是 `display: none`,量到的高度全是 0。
        `position: fixed` 讓它不佔版面,`print:hidden` 讓它不會被印出來。
      */}
      {blank ? null : (
        <div
          ref={measureRef}
          data-ar-detail-measure=""
          aria-hidden
          className="print:hidden"
          style={{
            position: 'fixed',
            left: '-10000px',
            top: 0,
            width: `${PAGE_CONTENT_WIDTH_MM}mm`,
          }}
        >
          <div data-measure="header">
            <ArDetailPrintHeader
              reportLabel={reportLabel}
              customer={customer}
              from={from}
              to={to}
              pageNo={1}
              pageCount={1}
            />
          </div>
          <PageTable
            columns={columns}
            rows={rows}
            rowKey={rowKey}
            indexes={[...rows.keys()]}
            growColumnKey={growColumnKey}
            footer={footer}
          />
          {/* 量測那一份也要排一次 summary,否則它的高度不會被算進最後一頁 */}
          {summary ? <div data-measure="summary">{summary}</div> : null}
        </div>
      )}

      <div className="hidden print:block" data-testid="ar-detail-print-pages">
        {pageList.map((indexes, page) => (
          <div
            key={page}
            data-testid="ar-detail-print-page"
            // 最後一塊不掛 break-after,否則會多印一張空白頁
            // (`globals.css` 的 `.print-customer-block` 記過同一條)
            style={page < pageList.length - 1 ? { breakAfter: 'page' } : undefined}
          >
            <ArDetailPrintHeader
              reportLabel={reportLabel}
              customer={customer}
              from={from}
              to={to}
              pageNo={page + 1}
              pageCount={pageList.length}
            />
            {blank ? (
              <BlankBody isLoading={isLoading} isError={isError} error={error} hint={emptyHint} />
            ) : (
              <>
                <PageTable
                  columns={columns}
                  rows={rows}
                  rowKey={rowKey}
                  indexes={indexes}
                  colWidths={fresh?.colWidths}
                  growColumnKey={growColumnKey}
                  // 合計列只掛在最後一頁的表格末尾(需求第 1 點)
                  footer={page === pageList.length - 1 ? footer : undefined}
                />
                {/* 合計說明區同理:表格之後、只印最後一頁(REQ-20260901012) */}
                {page === pageList.length - 1 ? summary : null}
              </>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

/** 載入中 / 失敗 / 沒資料的訊息 —— 文案與 `ReportTable` 的三態同一套,不另寫一份 */
function BlankBody({
  isLoading,
  isError,
  error,
  hint,
}: {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  hint: string;
}): JSX.Element {
  if (isLoading) return <div className="text-slate-500 text-sm">載入中...</div>;
  if (isError) {
    return (
      <div className="text-red-600 text-sm">
        載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
      </div>
    );
  }
  return <div className="text-slate-500 text-sm">{hint}</div>;
}

/**
 * 一頁的表格。表頭列每頁都印,合計列只有最後一頁才傳 `footer`。
 *
 * **不用 `<tfoot>`** —— 瀏覽器會把 `tfoot` 重印在每一頁底部,那正是需求第 1 點
 * 要修掉的行為(REQ-20260831008 §三 實測過)。合計列改成 `tbody` 的最後一列,
 * 樣式與 `ReportTable` 的合計列相同。
 *
 * 量測用的那一份不傳 `colWidths`(它就是欄寬的來源),頁面用的都傳。
 */
function PageTable<T>({
  columns,
  rows,
  rowKey,
  indexes,
  colWidths,
  growColumnKey,
  footer,
}: {
  columns: Array<ReportColumn<T>>;
  rows: T[];
  rowKey: (row: T) => string | number;
  indexes: number[];
  colWidths?: number[] | undefined;
  growColumnKey?: string | undefined;
  footer?: Array<ReactNode | null> | undefined;
}): JSX.Element {
  const fixed = colWidths != null && colWidths.length === columns.length;
  return (
    <table
      className="w-full bg-white"
      style={fixed ? { tableLayout: 'fixed' } : undefined}
    >
      {fixed ? (
        <colgroup>
          {colWidths.map((w, i) => (
            <col key={columns[i]?.key ?? i} style={{ width: `${w}px` }} />
          ))}
        </colgroup>
      ) : null}
      <thead>
        {/* 表頭樣式沿用 REQ-20260831008 的四項(縮一級字 / 不粗體 / 不折行 / 收 padding),
            但這裡直接寫在 `th` 上 —— 這份表格不是 `ReportTable`,沒有 `[&_th]:` 的必要 */}
        <tr className="border-b text-left text-slate-600">
          {columns.map((c) => (
            <th
              key={c.key}
              className={`${CELL_PAD} text-xs font-normal whitespace-nowrap ${alignClass(c.align)}`}
              // `width: 100%` 讓這一欄吃掉剩餘寬度,其餘欄位收斂到 max-content
              style={c.key === growColumnKey ? { width: '100%' } : undefined}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {indexes.map((i) => {
          const row = rows[i] as T;
          return (
            // `data-print-row`:量測時要能把品項列與合計列分開數(合計列也在 tbody 裡)
            <tr key={rowKey(row)} data-print-row="" className="border-b text-sm">
              {columns.map((c) => (
                <td key={c.key} className={`${CELL_PAD} ${cellClass(c.align)} ${c.className ?? ''}`}>
                  {c.render ? c.render(row) : c.value(row)}
                </td>
              ))}
            </tr>
          );
        })}
        {footer ? (
          <tr className="border-t-2 text-sm font-medium bg-slate-50" data-testid="ar-detail-print-total">
            {columns.map((c, i) => (
              <td
                key={c.key}
                className={`${CELL_PAD} ${cellClass(c.align)}${c.className ? ` ${c.className}` : ''}`}
              >
                {footer[i] ?? null}
              </td>
            ))}
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}
